/**
 * Account Setup Success Page
 * 
 * Displayed after successful account setup completion.
 * Shows success message and links to continue.
 * 
 * URL: /account-setup-success
 * 
 * @module pages/auth
 */

import { useLocation } from 'react-router';
import { TenantLogo } from '@butter/ui/components/data-display';
import { TenantLink } from '~/lib/navigation'

interface LocationState {
  message?: string;
  statusCode?: number;
}

/**
 * Account Setup Success Page
 * 
 * Shows:
 * - Success checkmark icon
 * - Congratulations message
 * - App download links (optional)
 * - Continue to booking button
 */
export function AccountSetupSuccessPage() {
  const location = useLocation();

  // Get message from location state (passed from account setup page)
  const locationState = location.state as LocationState | null;
  const message = locationState?.message || 'Your account has been successfully set up and you\'re now ready to use all features.';

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-12">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <TenantLogo />
          </div>

          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
            <svg 
              className="w-8 h-8 text-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-fg-primary mb-4">
            Account Setup Complete!
          </h1>

          {/* Message */}
          <p className="text-fg-quaternary mb-8">
            {message}
          </p>

          {/* Continue Button */}
          <TenantLink
            to='/'
            className="w-full py-3 px-4 bg-theme-600 text-white font-medium rounded-lg hover:bg-theme-700 focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 transition-colors"
          >
            Continue to Booking
          </TenantLink>
        </div>
      </div>
    </div>
  );
}
